import { useNavigate } from "react-router-dom";
import { Clapperboard, Crown, LogOut, Settings2, ShieldCheck, User, Users } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useProfiles } from "@/context/ProfileContext";
import { useGuest } from "@/context/GuestContext";
import { useCreatorOn } from "@/lib/platform";

export const ProfileMenu = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { guestOn } = useGuest();
  const creatorOn = useCreatorOn();
  const { profiles, activeProfile, selectProfile } = useProfiles();
  const others = (profiles || []).filter((p) => p.id !== activeProfile?.id);

  const signOut = async () => {
    await logout();
    navigate(guestOn ? "/" : "/login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="profile-menu-trigger" className="relative flex items-center gap-2 rounded-full outline-none">
          <img
            src={activeProfile?.avatar}
            alt={activeProfile?.name}
            className="h-10 w-10 rounded-full object-cover ring-1 ring-[#D4AF37] border border-black shadow-[0_0_16px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:scale-105"
          />
          {activeProfile?.is_kids && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded bg-[#D4AF37] px-1 text-[0.55rem] font-extrabold text-black">KIDS</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={12} className="w-60 glass bg-[#0B0B0D]/95 border-[#D4AF37]/30 text-white" data-testid="profile-menu">
        <DropdownMenuLabel className="text-xs text-[#8B8B8B] font-medium">Watching as <span className="text-[#FFD86A]">{activeProfile?.name}</span></DropdownMenuLabel>
        {others.map((p) => (
          <DropdownMenuItem key={p.id} data-testid={`switch-profile-${p.id}`} onSelect={() => { selectProfile(p.id); navigate("/"); }} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10">
            <img src={p.avatar} alt="" className="h-7 w-7 rounded-md object-cover" />
            <span className="truncate">{p.name}</span>
            {p.is_kids && <span className="chip ml-auto !text-[0.6rem]">KIDS</span>}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-[#D4AF37]/15" />
        <DropdownMenuItem data-testid="menu-switch-profile" onSelect={() => navigate("/profiles")} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10"><Users className="h-4 w-4 text-[#D4AF37]" /> Switch profile</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-manage-profiles" onSelect={() => navigate("/profiles?manage=1")} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10"><Settings2 className="h-4 w-4 text-[#D4AF37]" /> Manage profiles</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-account" onSelect={() => navigate("/account")} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10"><User className="h-4 w-4 text-[#D4AF37]" /> Account</DropdownMenuItem>
        {user?.role === "owner" && <DropdownMenuItem data-testid="menu-owner-center" onSelect={() => navigate("/owner")} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10"><Crown className="h-4 w-4 text-[#FFD86A]" /> Owner Control Center</DropdownMenuItem>}
        {["admin", "owner"].includes(user?.role) && <DropdownMenuItem data-testid="menu-admin-console" onSelect={() => navigate("/admin")} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Admin Console</DropdownMenuItem>}
        {creatorOn && ["admin", "creator", "owner"].includes(user?.role) && <DropdownMenuItem data-testid="menu-creator-studio" onSelect={() => navigate("/studio/uploads")} className="gap-3 cursor-pointer focus:bg-[#D4AF37]/10"><Clapperboard className="h-4 w-4 text-[#D4AF37]" /> Creator Studio</DropdownMenuItem>}
        <DropdownMenuSeparator className="bg-[#D4AF37]/15" />
        <DropdownMenuItem data-testid="menu-sign-out" onSelect={signOut} className="gap-3 cursor-pointer focus:bg-[#E50914]/15"><LogOut className="h-4 w-4 text-[#E50914]" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
